import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { setOrgOverride } from '@/api/client';
import { useAuth, type MeStatus } from '@/contexts/AuthContext';
import { useMyOrganizations, useOrgSwitch } from '@/hooks/useOrgSwitch';
import { isOrgRequired, isOrgScoped, surfaceForPath, SURFACES } from '@/surfaces';
import type { MyOrganization } from '@/types';

type GateUser =
  | {
      role?: string;
      staff?: boolean;
      actingOrgId?: string | null;
      orgOverrideRejected?: boolean;
    }
  | null
  | undefined;

/**
 * What the BFF makes of the org-switcher pick (#1171), going by what
 * /api/auth/me reported for a request carrying the same `X-Org-Override`,
 * not by the pick merely existing:
 *
 * - `'none'`: no pick saved.
 * - `'valid'`: the BFF acts in it. This is also the answer when /api/auth/me
 *   could not be read (`meStatus` `'error'`): keep the pick rather than block
 *   the app on it.
 * - `'stale'`: the BFF will not act in it (`orgOverrideRejected`). For a
 *   non-superuser it is outside their orgs, and the org-scoped surface
 *   answers 403 org_not_member. For a superuser it names an org that no
 *   longer exists, so every service would scope to an org with no data.
 *   For a non-superuser it is also stale when /api/auth/me acts in a
 *   different org.
 * - `'pending'`: /api/auth/me has not answered yet (`meStatus` `'pending'`).
 *
 * `meStatus` is AuthContext's (#1168): whether /api/auth/me has answered.
 */
export function pickStatus(
  user: GateUser,
  meStatus: MeStatus,
  override: string | null,
): 'none' | 'valid' | 'stale' | 'pending' {
  if (!override) return 'none';
  if (!user) return 'valid';
  if (meStatus === 'pending') return 'pending';
  if (meStatus === 'error') return 'valid';
  if (user.orgOverrideRejected) return 'stale';
  if (user.role !== 'superuser' && user.actingOrgId !== override) return 'stale';
  return 'valid';
}

/**
 * Whether the BFF will forward no `X-User-Org` for this session, mirroring
 * `authenticate` / `setVerifiedIdentityHeaders` (#1149):
 *
 * - a valid org-switcher pick is forwarded. A stale one (see `pickStatus`)
 *   counts as no org: the BFF refuses it, or forwards an org that does not
 *   exist;
 * - with no pick, a superuser acts in no org ("All Organizations");
 * - with no pick, anyone else acts in their only org. Staff in no org, and a
 *   member of several orgs (the BFF answers them 409
 *   org_selection_required), act in none: /api/auth/me reports
 *   `orgId` unset, which AuthContext exposes as `actingOrgId: null`.
 *
 * Until /api/auth/me answers, this assumes a non-superuser has an org, and
 * the gate shows a loading state on org-scoped pages instead (`orgGateReason`).
 * An org-less non-staff user never gets here: RequireAuth shows
 * NoOrganization.
 */
export function forwardsNoOrg(user: GateUser, meStatus: MeStatus, override: string | null): boolean {
  if (!user) return false;
  const pick = pickStatus(user, meStatus, override);
  if (pick === 'stale') return true;
  if (pick !== 'none') return false;
  if (user.role === 'superuser') return true;
  return user.actingOrgId === null;
}

/**
 * What the gate does on `pathname`, or null to show the page:
 *
 * - `'surface'` (#1149): a superuser in "All Organizations" on an
 *   `orgRequired` page. Every other surface works across all orgs for them.
 * - `'acting'` (#1171): anyone else the BFF forwards no org for (a member of
 *   several orgs with no pick, staff in no org) on any org-scoped page
 *   (`isOrgScoped`). The BFF answers the member 409 org_selection_required
 *   on every org-scoped route, and the services' requireOrgScope answers
 *   staff 403, so no surface works for them. `orgFree` pages (the account
 *   page) and paths outside the surfaces stay reachable.
 * - `'stale'` (#1171): a saved pick the BFF will not act in, on an
 *   org-scoped page. The gate clears it and shows the picker.
 * - `'pending'` (#1171): /api/auth/me has not answered yet, and the answer
 *   decides whether this org-scoped page can load. That is any non-superuser,
 *   or a superuser with a pick. The gate shows a loading line rather than
 *   mount a page whose calls may 409 or 403. A superuser with no pick does
 *   not wait: their gate depends only on their role.
 */
export function orgGateReason(
  user: GateUser,
  meStatus: MeStatus,
  override: string | null,
  pathname: string,
): 'surface' | 'acting' | 'stale' | 'pending' | null {
  if (!user) return null;
  const waitsForMe = user.role !== 'superuser' || override !== null;
  if (waitsForMe && meStatus === 'pending') return isOrgScoped(pathname) ? 'pending' : null;
  if (pickStatus(user, meStatus, override) === 'stale') return isOrgScoped(pathname) ? 'stale' : null;
  if (!forwardsNoOrg(user, meStatus, override)) return null;
  if (user.role === 'superuser') return isOrgRequired(pathname) ? 'surface' : null;
  return isOrgScoped(pathname) ? 'acting' : null;
}

/** The picker's line for a member of several orgs with no pick (#1171). */
export const CHOOSE_ACTING_ORG = 'You belong to several organizations — choose the one to work in.';

/** The picker's line when the saved pick is no longer one of the user's orgs (#1171). */
export const STALE_PICK = 'The organization you last picked is no longer available — choose the one to work in.';

/**
 * The org picker in place of a page the session cannot load without an org
 * (see `orgGateReason`). The page never mounts, so none of its queries fire.
 * Picking switches the way the header's org menu does (`useOrgSwitch`).
 */
export default function OrgRequiredGate({ children }: { children: React.ReactNode }) {
  const { user, meStatus } = useAuth();
  const { pathname } = useLocation();
  const { selectedOrgId, switchOrg } = useOrgSwitch();
  const reason = orgGateReason(user, meStatus, selectedOrgId, pathname);
  const gated = reason !== null && reason !== 'pending';
  const { data: organizations, isLoading, isError } = useMyOrganizations(gated);

  // A pick the BFF will not act in is cleared, so the next load starts
  // clean. There is no reload here: the page never mounted, and choosing in
  // the picker reloads anyway.
  const stale = reason === 'stale';
  useEffect(() => {
    if (stale) setOrgOverride(null);
  }, [stale]);

  if (reason === 'pending') {
    return <p className="text-sm text-jeket-silver-muted">Loading your organization…</p>;
  }
  if (!gated) return <>{children}</>;

  const surface = surfaceForPath(SURFACES, pathname);
  const label = surface?.label ?? 'This page';

  return (
    <section
      aria-labelledby="org-required-title"
      className="max-w-lg mx-auto mt-12 bg-jeket-black-surface border border-jeket-gold/20 rounded-lg p-6"
    >
      <div className="flex items-center gap-2 mb-2">
        <Building2 size={18} className="text-jeket-gold" />
        <h1 id="org-required-title" className="text-lg font-semibold text-jeket-silver-bright">
          Choose an organization
        </h1>
      </div>
      {reason === 'surface' && (
        <p className="text-sm text-jeket-silver-muted mb-4">
          {label} is per organization — pick one to {surface?.orgRequiredHint ?? 'continue'}.
        </p>
      )}
      {reason === 'acting' && organizations && organizations.length > 0 && (
        <p className="text-sm text-jeket-silver-muted mb-4">{CHOOSE_ACTING_ORG}</p>
      )}
      {reason === 'stale' && <p className="text-sm text-jeket-silver-muted mb-4">{STALE_PICK}</p>}
      {isLoading && <p className="text-sm text-jeket-silver-muted">Loading organizations…</p>}
      {isError && <p className="text-sm text-jeket-error">Could not load organizations.</p>}
      {organizations && organizations.length === 0 && (
        <p className="text-sm text-jeket-silver-muted">
          {user?.staff && user.role !== 'superuser'
            ? "You aren't a member of any organization. Ask an organization's admin to add you, or use the staff console at corp.jeket.com."
            : 'There are no organizations yet.'}
        </p>
      )}
      {organizations && organizations.length > 0 && (
        <ul className="flex flex-col gap-1">
          {organizations.map((org: MyOrganization) => (
            <li key={org.id}>
              <button
                type="button"
                onClick={() => switchOrg(org.id)}
                className="w-full px-3 py-2 text-left text-sm rounded-md text-jeket-silver-bright bg-jeket-black-elevated hover:bg-jeket-gold/10 hover:text-jeket-gold transition-colors"
              >
                {org.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
